// library imports
import { Empty, message, Segmented, Flex } from "antd";
import { useEffect, useState, useContext } from "react";
import dayjs from "dayjs";
// style imports
import "../../styles/Dashboard/DashboardPage.css";
// component imports
import Analytics from "../../components/analytics/Analytics";
import CustomLineBarChart from "../../components/common/CustomLineBarChart";
import { VerticalDateFilter } from "../../components/common/DateFilter";
// context imports
import { IndexContext } from "../../context/IndexContext";
// hook imports
import useFetch from "../../hooks/useFetch";
// config imports
import {
  DARK_BLUE,
  LIGHT_BLUE,
  LIGHT_LIGHT_BLUE,
  DARK_GREEN,
  LIGHT_GREEN,
  ORANGE,
  DARK_THEME_DARK_BLUE,
  PURPLE,
} from "../../configs/configs";

const dateTypeOptions = [
  { label: "Day", value: "day" },
  { label: "Month", value: "month" },
  { label: "Year", value: "year" },
];

export default function EnergyDemandPage() {
  const [fetchWrapper] = useFetch();
  const {
    siteSelected,
    notificationApi,
    isLightTheme,
    subscribeAiBess,
    hasGensetSelected,
    hasTnbSelected,
  } = useContext(IndexContext);

  const [isTrendingDataLoading, setIsTrendingDataLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [startDateSelection, setStartDateSelection] = useState(
    dayjs().subtract(6, "day"),
  );
  const [endDateSelection, setEndDateSelection] = useState(dayjs());

  const [dateTypeSelection, setDateType] = useState(dateTypeOptions[0].value);

  const [siteCommissioningDate, setSiteCommissioningDate] = useState(null);

  const [energyDemandData, setEnergyDemandData] = useState([]);
  const [loadEnergyData, setLoadEnergyData] = useState([]);

  const [chartRange, setChartRange] = useState([]);
  const [chartDomain, setChartDomain] = useState([]);

  const onFilterClick = () => {
    if (startDateSelection > endDateSelection) {
      message.error("End date must be later than or same as start date.");
    } else if (endDateSelection.diff(startDateSelection, "day") > 31) {
      message.warning("Date range cannot exceed 31 days.");
    } else {
      getEnergyDemandData();
    }
  };

  const onExportClick = () => {
    if (energyDemandData.length < 1) {
      message.error("No data to export");
    } else {
      if (energyDemandData.length < 1 && loadEnergyData.length < 1) {
        message.error("No data to export");
      } else {
        downloadEnergyDemandData();
      }
    }
  };

  /** API to get the energy demand data */
  function getEnergyDemandData() {
    setIsTrendingDataLoading(true);
    fetchWrapper({
      endpoint_url: "energy_demand/getEnergyDemandData",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        start_date: startDateSelection.format("YYYY-MM-DD HH:mm:ss"),
        end_date: endDateSelection.format("YYYY-MM-DD HH:mm:ss"),
        date_selection: dateTypeSelection,
      },

      onSuccess: (response) => {
        setEnergyDemandData(response.data.energy_trending_data);
        setLoadEnergyData(response.data.load_data);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get energy demand data.",
          description: response.statusText,
        });
      },

      onFinish: () => {
        setIsTrendingDataLoading(false);
      },
    });
  }

  /** API to download the energy demand data */
  function downloadEnergyDemandData() {
    setDownloadLoading(true);

    fetchWrapper({
      endpoint_url: "energy_demand/downloadEnergyDemandData",
      method: "POST",
      json_output: false,
      body: {
        energy_demand_data: energyDemandData,
        load_data: loadEnergyData,
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        if (response.headers.get("content-type") !== "application/json") {
          // no issue with the response. returned a spreadsheet
          response.blob().then((blob) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Energy_Demand_Data_${dayjs().format(
              "YYYYMMDD_HHmmss",
            )}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
          });
        }
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to download file",
        });
      },

      onFinish: () => {
        setDownloadLoading(false);
      },
    });
  }

  /* API to get site commissioning date */
  function getSiteCommissioningDate() {
    fetchWrapper({
      endpoint_url: "common/getSiteCommissioningDate",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        setSiteCommissioningDate(response.data.site_commissioning_date);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get site commissioning date.",
          description: response.statusText,
        });
      },
    });
  }

  useEffect(() => {
    setEnergyDemandData([]);
    setLoadEnergyData([]);
  }, [siteSelected]);

  useEffect(() => {
    if (subscribeAiBess === true) {
      if (hasGensetSelected === true) {
        if (hasTnbSelected === false) {
          setChartDomain([
            "PV (Self-Consumption)",
            "BESS Discharge",
            "BESS Charge",
            "Load",
            "Genset",
          ]);
          setChartRange(
            isLightTheme
              ? [DARK_GREEN, LIGHT_BLUE, LIGHT_LIGHT_BLUE, ORANGE, PURPLE]
              : [DARK_GREEN, LIGHT_BLUE, LIGHT_LIGHT_BLUE, ORANGE, PURPLE],
          );
        } else {
          setChartDomain([
            "PV (Self-Consumption)",
            "PV (Export)",
            "BESS Discharge",
            "BESS Charge",
            "TNB",
            "Load",
            "Genset",
          ]);
          setChartRange(
            isLightTheme
              ? [
                  DARK_GREEN,
                  LIGHT_GREEN,
                  LIGHT_BLUE,
                  LIGHT_LIGHT_BLUE,
                  DARK_BLUE,
                  ORANGE,
                  PURPLE,
                ]
              : [
                  DARK_GREEN,
                  LIGHT_GREEN,
                  LIGHT_BLUE,
                  LIGHT_LIGHT_BLUE,
                  DARK_THEME_DARK_BLUE,
                  ORANGE,
                  PURPLE,
                ],
          );
        }
      } else {
        setChartDomain([
          "PV (Self-Consumption)",
          "PV (Export)",
          "BESS Discharge",
          "BESS Charge",
          "TNB",
          "Load",
        ]);
        setChartRange(
          isLightTheme
            ? [
                DARK_GREEN,
                LIGHT_GREEN,
                LIGHT_BLUE,
                LIGHT_LIGHT_BLUE,
                DARK_BLUE,
                ORANGE,
              ]
            : [
                DARK_GREEN,
                LIGHT_GREEN,
                LIGHT_BLUE,
                LIGHT_LIGHT_BLUE,
                DARK_THEME_DARK_BLUE,
                ORANGE,
              ],
        );
      }
    } else {
      if (hasGensetSelected === true) {
        setChartDomain([
          "PV (Self-Consumption)",
          "PV (Export)",
          "TNB",
          "Load",
          "Genset",
        ]);
        setChartRange(
          isLightTheme
            ? [DARK_GREEN, LIGHT_GREEN, DARK_BLUE, ORANGE, PURPLE]
            : [DARK_GREEN, LIGHT_GREEN, DARK_THEME_DARK_BLUE, ORANGE, PURPLE],
        );
      }
    }
  }, [subscribeAiBess, hasTnbSelected, hasGensetSelected]);

  useEffect(() => {
    if (siteSelected) {
      getSiteCommissioningDate();
    }
  }, [siteSelected]);

  // when site commissioning date changes, need to update start date
  useEffect(() => {
    if (siteCommissioningDate) {
      setStartDateSelection(
        dayjs(
          Math.max(
            dayjs().subtract(6, "day").valueOf(),
            dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf(),
          ),
        ),
      );
    }
  }, [siteCommissioningDate]);

  return (
    <Analytics
      title={"Energy Demand Distribution"}
      contentComponent={
        energyDemandData.length > 0 ? (
          <CustomLineBarChart
            dataLine={loadEnergyData.map((item) => {
              const newItem = { ...item };
              for (const key in newItem) {
                if (newItem[key] === "-") {
                  newItem[key] = null;
                }
              }
              return newItem;
            })}
            dataBar={energyDemandData.map((item) => {
              const newItem = { ...item };
              for (const key in newItem) {
                if (newItem[key] === "-") {
                  newItem[key] = null;
                }
              }
              return newItem;
            })}
            xAxisLine="date"
            yAxisLine="Load"
            xAxisBar="date"
            yAxisBar="value"
            xAxisTitleBar="Time"
            yAxisTitleBar="Energy (kWh)"
            lineColor="type"
            barColor="type"
            barChartRange={chartRange}
            barChartDomain={chartDomain}
            lineChartColor={ORANGE}
          />
        ) : (
          <Empty />
        )
      }
      isTrendingDataLoading={isTrendingDataLoading}
      filterComponent={
        <div className="analytics-picker-type-segmented">
          {/* <Flex vertical={true} gap={"8px"}>
            <span>Graph Type Selection</span>
            <Segmented
              style={{ marginBottom: "12px" }}
              options={dateTypeOptions}
              onChange={(value) => {
                setDateType(value);
              }}
              value={dateTypeSelection}
            />
          </Flex> */}

          <VerticalDateFilter
            startDateSelection={startDateSelection}
            setStartDateSelection={setStartDateSelection}
            endDateSelection={endDateSelection}
            setEndDateSelection={setEndDateSelection}
            searchLoading={isTrendingDataLoading}
            downloadLoading={downloadLoading}
            onFilterClick={onFilterClick}
            onExportClick={onExportClick}
            pickerType={dateTypeSelection}
            minDate={dayjs(siteCommissioningDate, "YYYY-MM-DD")}
            maxDate={dayjs()}
          />
        </div>
      }
    />
  );
}
